'use client';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { Button } from '@//components/Button';
import { PopoverMenu, PopoverMenuButton } from '@//components/PopoverMenu';
import type { ElasticSearchResponseDocumentListSortOption } from '@//ElasticSearch';
import styles from './FilterSort.module.scss';

export interface FilterSortOption {
  value: string;
  displayName: string;
  query: string;
  selected?: boolean;
}

const SORT_VALUE_TO_TRANSLATION_KEY: Record<string, string> = {
  '': 'newest',
  itemSortDesc: 'newest',
  priceDefaultAsc: 'lowestPrice',
  priceDefaultDesc: 'highestPrice',
  modelYearDesc: 'modelYearNewest',
  modelYearAsc: 'modelYearOldest',
  mileageAsc: 'lowestMileage',
  mileageDesc: 'highestMileage',
  privateLeasingPriceAsc: 'lowestPrivateLeasingPrice',
  privateLeasingPriceDesc: 'highestPrivateLeasingPrice',
  businessLeasingPriceAsc: 'lowestBusinessLeasingPrice',
  businessLeasingPriceDesc: 'highestBusinessLeasingPrice',
};

interface FilterSortProps {
  sortOptions: ElasticSearchResponseDocumentListSortOption[];
  currentSort: string;
  defaultSort?: string;
  extractSortValue?: (query: string) => string;
  onSortChange: (value: string) => void;
  getSortIcon: (sortValue: string) => React.ReactNode;
  buttonTitle: string;
  buttonAriaLabel: string;
  buttonPrefix: string;
  buttonUnknown: string;
  menuAriaLabel: string;
  menuOptionTitle: string;
}

const defaultExtractSortValue = (query: string) => {
  const params = new URLSearchParams(query);
  return params.get('sort') || '';
};

const FilterSort: React.FC<FilterSortProps> = ({
  sortOptions = [],
  currentSort,
  defaultSort = '',
  extractSortValue = defaultExtractSortValue,
  onSortChange,
  getSortIcon,
  buttonTitle,
  buttonAriaLabel,
  buttonPrefix,
  buttonUnknown,
  menuAriaLabel,
  menuOptionTitle,
}) => {
  const t = useTranslations('utils.search.sortOptions');

  const handleSortChange = useCallback(
    (value: string) => {
      onSortChange(value);
    },
    [onSortChange],
  );

  const processedOptions = useMemo((): FilterSortOption[] => {
    return sortOptions.map((option) => {
      const sortValue = extractSortValue(option.query);
      const translationKey = SORT_VALUE_TO_TRANSLATION_KEY[sortValue];
      const displayName = translationKey ? t(translationKey as 'newest') : option.displayName;
      return {
        value: sortValue,
        displayName,
        query: option.query,
        selected: option.selected,
      };
    });
  }, [sortOptions, extractSortValue, t]);

  const currentSortOption = useMemo(() => {
    if (!currentSort) {
      // When no sort is applied, find the default sort option
      return processedOptions.find((option) => option.value === defaultSort) || processedOptions[0];
    }

    // Find the option that matches the current sort value
    return processedOptions.find((option) => option.value === currentSort) || processedOptions[0];
  }, [currentSort, processedOptions, defaultSort]);

  return (
    <PopoverMenu
      trigger={
        <Button
          variant="no-bg"
          size="md"
          title={buttonTitle}
          aria-label={buttonAriaLabel.replace(
            '{currentSort}',
            currentSortOption?.displayName || buttonUnknown,
          )}
        >
          {getSortIcon(currentSort)}
          <span className={styles.label}>
            <span className={styles.buttonLabelPrefix}>{buttonPrefix}</span>{' '}
            <span className={styles.buttonLabelValue}>
              {currentSortOption?.displayName || buttonUnknown}
            </span>
          </span>
        </Button>
      }
      ariaLabel={menuAriaLabel}
      side="bottom"
      align="end"
      sideOffset={8}
    >
      {processedOptions.map((option) => (
        <PopoverMenuButton
          key={option.value}
          label={option.displayName}
          title={menuOptionTitle.replace('{option}', option.displayName)}
          onClick={() => handleSortChange(option.value)}
          isActive={option.value === currentSort}
        />
      ))}
    </PopoverMenu>
  );
};

export default FilterSort;
